import { Layout, Table, Input, Modal } from "antd";
import { Content } from "antd/es/layout/layout";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@components/Button";
import Header from "@components/Header";
import { InnerContents } from "@components/InnerContents";
import { SideMenu } from "@components/SideMenu";
import { COLOR_GREEN, COLOR_WHITE } from "@static/COLORS";
import { CONTENT_STYLES } from "@static/CONTENT_STYLES";
import { KEY_USERS_MANAGEMENTS_USERS_ADD } from "@static/PERMISSION_KEY";
import { FILTER_SIZE, MENU_SIZE, MODAL_SIZE, PAGE_SIZE } from "@static/SIZE";
import { getUserTableColumns } from "@static/TABLE/BASE_USER_TABLE_COLUMNS";
import { getAuthUserPermissions } from "@utils/getAuthUserPermissions";
import { getAuthUserProfile } from "@utils/getAuthUserProfile";
import { getUserExtraCustomers } from "@utils/getUserExtraCustomers";
import { fetchAPI } from "@utils/https/fetchAPI";
import { useShowConfirmation } from "@utils/useShowConfirmation";

import { FormCreate } from "./FormCreate";
import { FormEdit } from "./FormEdit";
import { FormManageCustomers } from "./FormManageCustomers";
import { FormManageLocation } from "./FormManageLocation";

const SectionCreate = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const SectionMainAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${COLOR_WHITE};
  padding: 8px;
  border-radius: 6px;
  height: 50px;
  box-sizing: border-box;
`;

export default () => {
  const allCustomers = getUserExtraCustomers();

  const showConfirmation = useShowConfirmation();
  const permission = getAuthUserPermissions();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filterText, setFilterText] = useState("");
  const [dataList, setDataList] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState({
    isOpen: false,
    modalType: null,
  });
  const authUserProfile = getAuthUserProfile();

  const user_id = _.get(authUserProfile, ["id"]);
  const user_type = _.get(authUserProfile, ["user_type"]);
  const category_customer_id = _.get(authUserProfile, ["category_customer_id"]);

  const [roleOptions, setRoleOptions] = useState(null);
  const [customerOptions, setCustomerOptions] = useState(null);

  const [locationList, setLocationList] = useState([]);

  const [selectedData, setSelectedData] = useState(null);

  const onHandleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const onHandleFilterChange = (e) => {
    const value = _.get(e, ["target", "value"]);
    setFilterText(value);
  };

  const onHandleCreate = ({ modalType }) => {
    setIsModalVisible({ isOpen: true, modalType });
  };

  const onHandleEdit = ({ item }) => {
    setSelectedData(item);
    setIsModalVisible({ isOpen: true, modalType: "edit" });
  };

  const onHandleManageLocation = ({ item }) => {
    setSelectedData(item);
    setIsModalVisible({ isOpen: true, modalType: "manage location" });
  };

  const onHandleManageCustomer = ({ item }) => {
    setSelectedData(item);
    setIsModalVisible({ isOpen: true, modalType: "manage customer" });
  };

  const onHandleDelete = async ({ item }) => {
    const id = _.get(item, ["id"]);
    const resp = await fetchAPI({
      method: "delete",
      path: `/api/v1/users/${id}`,
      isAuth: true,
    });
    if (_.get(resp, ["status"])) {
      toast.success("Delete User Success.");
      const updateDataList = _.chain(dataList)
        .filter((item) => {
          return _.get(item, ["id"]) !== id;
        })
        .map((item, index) => {
          return { ...item, index: index + 1 };
        })
        .value();
      setDataList(updateDataList);
    }
  };

  const filteredDataSource = _.filter(dataList, (item) => {
    const itemName = _.chain(item).get(["username"]).toLower().value();
    const lowerFilterText = _.toLower(filterText);
    return _.includes(itemName, lowerFilterText);
  });

  const initData = async () => {
    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/users",
      isAuth: true,
      data: { user_type, category_customer_id: allCustomers },
    });

    if (_.get(resp, ["status"])) {
      const results = _.chain(resp)
        .get(["result", "users"])
        .map((item, index) => {
          return { key: index, index: index + 1, ...item };
        })
        .value();
      console.log("results :>> ", results);
      const initDropdownRoles = _.get(resp, [
        "result",
        "dropdownCategoryRoles",
      ]);

      const initDropdownCategoryCustomers = _.get(resp, [
        "result",
        "dropdownCategoryCustomers",
      ]);

      const initLocationList = _.get(resp, ["result", "locations"], []);

      const initDataList = _.filter(results, (item) => {
        const isRootUser = _.get(item, ["is_root_user"]);
        const id = _.get(item, ["id"]);
        if (user_type === "OWNER") {
          return id !== user_id;
        }

        return !isRootUser && id !== user_id;
      });

      setLocationList(initLocationList);
      setCustomerOptions(initDropdownCategoryCustomers);
      setRoleOptions(initDropdownRoles);
      setDataList(initDataList);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  console.log("dataList :>> ", dataList);

  return (
    <Layout hasSider>
      <SideMenu />
      <Layout style={{ marginLeft: MENU_SIZE }}>
        <Header />
        <Content style={CONTENT_STYLES}>
          <InnerContents>
            <SectionMainAction>
              <Input
                placeholder="Filter by name"
                value={filterText}
                onChange={(e) => onHandleFilterChange(e)}
                style={{ width: FILTER_SIZE }}
              />
              <SectionCreate>
                {_.includes(permission, KEY_USERS_MANAGEMENTS_USERS_ADD) && (
                  <Button
                    onClick={() => onHandleCreate({ modalType: "create" })}
                    color={COLOR_GREEN}
                    fontColor={COLOR_WHITE}
                    padding="7px 15px"
                  >
                    Add User
                  </Button>
                )}
              </SectionCreate>
            </SectionMainAction>

            <Table
              dataSource={filteredDataSource}
              columns={getUserTableColumns({
                userType: user_type,
                permission,
                onHandleEdit,
                onHandleDelete,
                onHandleManageLocation,
                onHandleManageCustomer,
                showConfirmation,
              })}
              pagination={{
                position: ["bottomRight"],
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
              }}
              // scroll={{
              //   x: 1500,
              // }}
              onChange={onHandleTableChange}
            />
          </InnerContents>
        </Content>
      </Layout>
      <Modal
        width={MODAL_SIZE}
        destroyOnClose={true}
        title={`${_.chain(isModalVisible)
          .get(["modalType"])
          .capitalize()
          .value()} user`}
        open={_.get(isModalVisible, ["isOpen"])}
        footer={null}
        onCancel={() =>
          setIsModalVisible((prev) => ({ ...prev, isOpen: false }))
        }
      >
        {_.get(isModalVisible, ["modalType"]) === "create" && (
          <FormCreate
            userProfile={authUserProfile}
            roleOptions={roleOptions}
            customerOptions={customerOptions}
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            dataList={dataList}
          />
        )}
        {_.get(isModalVisible, ["modalType"]) === "edit" && (
          <FormEdit
            userProfile={authUserProfile}
            roleOptions={roleOptions}
            customerOptions={customerOptions}
            selectedData={selectedData}
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            setSelectedData={setSelectedData}
          />
        )}
        {_.get(isModalVisible, ["modalType"]) === "manage location" && (
          <FormManageLocation
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            setSelectedData={setSelectedData}
            selectedData={selectedData}
            locationList={locationList}
          />
        )}

        {_.get(isModalVisible, ["modalType"]) === "manage customer" && (
          <FormManageCustomers
            setIsModalVisible={setIsModalVisible}
            setDataList={setDataList}
            setSelectedData={setSelectedData}
            selectedData={selectedData}
            locationList={locationList}
            customerOptions={customerOptions}
          />
        )}

        {/* customerOptions */}
      </Modal>
    </Layout>
  );
};
